import { buildExportMatrix, buildFooterRows, exportCellValue } from './exportClientView'
import { thousandsText, withoutThousandsSeparators } from './reportGridUtils'

/**
 * Client-side "export what you see" -> PDF (CC-49594). Sibling of the CSV/Excel
 * client exports in exportClientView: consumes the same published grid `view`,
 * the same scope flags ({ honorColumns, honorFilters }), and the same `meta`
 * header lines — so a PDF export always matches what the other formats say.
 *
 * jsPDF + jspdf-autotable are heavy; load them lazily (only when the user
 * actually exports a PDF) so importing this module — and every test that
 * transitively imports it — never has to resolve them, and Vite can code-split
 * the ~130KB-gzip pair out of the main bundle. (Same lazy pattern as ExcelJS.)
 *
 * Why not print CSS: Chrome does not implement CSS Paged Media margin boxes /
 * counter(pages), so "Page X of Y" footers are impossible via window.print().
 * autotable gives repeating column headers per page + a didDrawPage hook, and
 * jsPDF's putTotalPages() back-fills the Y in "Page X of Y".
 *
 * meta: { title, filtersLine, generatedLine, firmLine,
 *         orientation? ('portrait'|'landscape', default: auto — landscape
 *         when the visible column count exceeds 7) }
 * Layout per Jonathan's 7/8 feedback: firm name is H1 (largest), report name
 * H2, no AI/summary subtitle, filters-applied block (wrapped), and a single
 * "Generated … · Printed by …" row.
 */

// Mirror the CSV/Excel display formats so PDF numbers match the report exactly:
// currency columns render "$1,234.56" with accounting-style negatives "($1,234.56)".
// Spec 127 §4.6: a column that DECLARES `percent` prints its points with a "%"
// ("-88.6%", as the screen), checked before currency so a Show-$ choice can
// never put a dollar sign on a percentage.
const fmtNumber = (v, col) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return v == null ? '' : String(v)
  const decimals = Number.isFinite(col?.decimals) ? col.decimals : 2
  const s = Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    // Spec 212: "Show commas in numbers" off prints "1234.56", as the screen.
    useGrouping: col?.grouping !== false,
  })
  if (col?.percent) return `${v < 0 ? '-' : ''}${s}%`
  if (col?.currency) return v < 0 ? `($${s})` : `$${s}` // accounting-style negatives
  return `${v < 0 ? '-' : ''}${s}`
}

// jsPDF's built-in Helvetica is WinAnsi-only: arrows and other non-encodable
// glyphs render as mojibake (a date-range "->" arrow printed as "!'").
// The single angle quotes \u2039 \u203A ARE WinAnsi (0x8B / 0x9B \u2014 jsPDF writes them as
// such, measured) and are kept: Aged A/R's nested group heading reads
// "Client \u203A Attorney \u203A Invoice" on screen, and stripping the \u203A printed
// "Client  Attorney" (review of spec 127).
// Spec 133: \x0A (a line feed) is kept too. It is not a glyph \u2014 autotable prints a
// cell one line per "\n" \u2014 and stripping it is what ran AP Detail's two-line
// Vendor / Bill cell together into "Kayveo82". exportText (exportCell.js) has
// already normalised every CR LF / CR to "\n" and trimmed an outer break.
export const winAnsiSafe = (t) =>
  String(t ?? '')
    .replace(/[\u2192\u21D2\u2794\u279C]/g, 'to') // arrows -> "to"
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^\n\x20-\x7E\u00B7\u2013\u2014\u2039\u203A]/g, '') // keep LF + ASCII + middle dot + dashes + \u2039 \u203A

// A text cell as printed. Spec 211: a column the screen groups but the view
// writes as text (`thousands` \u2014 a left-aligned DECIMAL) prints grouped digits,
// so paper reads "1,234.50" where the grid does.
// Spec 212: with "Show commas in numbers" off (`grouping: false`) that column
// prints with every separator stripped instead, CB's own included.
const pdfText = (v, col) => {
  if (!col?.thousands) return winAnsiSafe(v)
  const text = String(v ?? '')
  return winAnsiSafe(col.grouping === false ? withoutThousandsSeparators(text) : thousandsText(text, col.label))
}

const PAGE = { marginX: 40, marginTop: 48, marginBottom: 44 } // pt, letter

/**
 * CC-50047 — right-align numeric cells in the table FOOT.
 *
 * `columnStyles` does not reach the foot (autotable's section styles win there),
 * so every total rendered LEFT-aligned while the data rows above it were
 * right-aligned. Confirmed on page 7 of QA's Timekeeper Analysis PDF: 143.59,
 * 51.90, 387.86 and 138,735.46 all hug the LEFT edge of their column while the
 * 0.00s above them hug the right — which reads as "the last few values appear
 * shifted".
 *
 * The cell INDEXES were correct all along: every total sits under its proper
 * header. So this is a pure alignment defect — NOT the footer-key mismatch first
 * suspected, and NOT CC-50024's mixed-unit column (that one is about a column
 * whose PTD row is hours and YTD row is currency, which is V1-by-design).
 *
 * Exported so the rule is testable without driving jsPDF layout.
 */
export const footAlignCell = (cols) => (data) => {
  if (data?.section === 'foot' && cols[data.column?.index]?.numeric) {
    data.cell.styles.halign = 'right'
  }
}

// ---- spec 127 §4.6: one grouped body for every grouped view -----------------
//
// A grouped grid publishes its body in render order as `outline` (the screen's
// filters applied) and `outlineAll` (none): heading / row / subtotal entries, with
// collapse and statement roll-up already applied (groupedExportView.js). The PDF
// prints THAT — CB's headers, CB's rows, CB's footer lines, verbatim — instead of
// a flat dump of leaf rows that dropped every heading and subtotal on all 86
// grouped reports (Robin, A3/A4). Nothing here computes a figure.

/** Label-column indent: 4pt + 10pt per group level / tree depth, capped. */
export const PDF_INDENT = { base: 4, step: 10, max: 40 }
const indentPadding = (steps) => ({
  top: 4,
  right: 4,
  bottom: 4,
  left: PDF_INDENT.base + Math.min(PDF_INDENT.max, Math.max(0, steps) * PDF_INDENT.step),
})

/**
 * The outline the PDF prints for this scope, or null for the flat body.
 *
 * Grouped whenever the chosen outline has any group structure — a heading OR a
 * subtotal line. (Spec 127 §4.6 said "a heading"; report 50 hides every group
 * header, so its outline has none, and the heading rule would have dropped all of
 * its per-timekeeper subtotal lines — the lines that report exists to print.)
 * Flat otherwise: the flat grid publishes no outline, and a grouped grid over
 * ungrouped rows (the Balance Sheet's flat fallback) has only row entries.
 * `respectCollapse: false` (no product caller) keeps meaning "fully expanded
 * leaves" by selecting the flat body.
 */
export const pdfOutline = (view, scope = {}) => {
  if (scope?.respectCollapse === false) return null
  const outline = scope?.honorFilters ? view?.outline : view?.outlineAll
  if (!Array.isArray(outline)) return null
  return outline.some((e) => e?.type === 'heading' || e?.type === 'subtotal') ? outline : null
}

/**
 * The column the grouped body indents: the first exported TEXT column that
 * carries a label on a subtotal line or a "Total {parent}" row — on the
 * statements that is the tree column (CB's "Total Income", "Total: Bank" sit
 * there, never in Account Number); otherwise the first text column. The view
 * carries no tree-column id, and with the tree column hidden this falls back to
 * the first text column, as §4.6 says.
 */
export const pdfLabelColumn = (entries, cols) => {
  const textIdx = cols.map((c, i) => (c?.numeric ? -1 : i)).filter((i) => i >= 0)
  const carriesLabel = (i) => entries.some((e) => (e.type === 'subtotal' || e.kind === 'total')
    && String(e.values?.[cols[i].key] ?? '').trim() !== '')
  return textIdx.find(carriesLabel) ?? textIdx[0] ?? 0
}

/**
 * Keep-together blocks, as { start: length } over `entries`: every heading chain
 * plus the first entry after it (and, when that entry opens a multi-line footer
 * block, the whole block), and every multi-line footer block on its own. The
 * report foot (`type: 'foot'` entries, always last) closes the body and stays
 * with the entry — and the block — printed just before it.
 */
export const keepTogetherBlocks = (entries) => {
  const spans = new Map()
  for (let i = 0; i < entries.length;) {
    let j = i
    while (j < entries.length && entries[j].type === 'heading') j++
    let end = Math.min(j, entries.length - 1)
    const first = entries[j]
    if (first?.type === 'subtotal' && first.line === 0) {
      while (end + 1 < entries.length
        && entries[end + 1].type === 'subtotal'
        && entries[end + 1].level === first.level
        && entries[end + 1].line === entries[end].line + 1) end++
    }
    if (end > i) spans.set(i, end - i + 1)
    i = Math.max(i + 1, end + 1)
  }
  const firstFoot = entries.findIndex((e) => e.type === 'foot')
  if (firstFoot !== -1) {
    let start = Math.max(0, firstFoot - 1)
    for (const [s, len] of spans) if (s <= start && start < s + len) start = s
    for (const s of [...spans.keys()]) if (s >= start) spans.delete(s)
    const last = entries.length - 1
    if (last > start) spans.set(start, last - start + 1)
  }
  return spans
}

/**
 * The grouped body as autotable rows, the keep-together gutter cell appended.
 *
 *  - heading   one bold cell spanning every column, indented per level
 *  - row       its values; the label column indented per (level + depth); a
 *              "Total {parent}" row (kind 'total') bold
 *  - subtotal  bold, verbatim; line 0 carries a hairline rule over its figures
 *  - foot      the report footer block (`footerRows`, already aligned to `cols` by
 *              buildFooterRows from the full-scope matrix), bold, as the body's
 *              closing lines
 * `summaryOnly` = headings and every subtotal line at every level, no rows.
 *
 * Keep-with-next without a second layout pass: autotable never splits a row that
 * carries a rowSpan cell across a page ("a new page is required whenever a rowspan
 * row don't fit a page"), and a rowSpan cell is as tall as the rows it spans. So a
 * gutter column — never drawn — whose one cell spans a heading chain and the entry
 * after it moves the heading to the next page with that entry instead of leaving
 * it orphaned at the bottom (prototype `PDF\design-DD\protoPdf.mjs`, measured).
 *
 * Why the foot is printed in the body and not as autotable's `foot`: autotable
 * reserves the foot's height only while laying out the LAST body row, never while
 * testing whether a rowSpan block fits. A block ending on the last row therefore
 * "fit", printed its heading, and then lost its last row to the next page — the
 * orphan the gutter exists to prevent (measured on P&L 35 with parents rolled up:
 * "Other Expenses" alone at the foot of page 1, its "Total Other Expenses" on page
 * 2). As body lines the foot joins the final block instead, and prints exactly
 * where autotable's last-page foot did: under the last line.
 */
export const groupedPdfBody = (outline, cols, { summaryOnly = false, footerRows = [] } = {}) => {
  const n = cols.length
  let entries = (outline || []).filter((e) => e && (e.type !== 'heading' || String(e.text ?? '').trim() !== ''))
  if (summaryOnly) entries = entries.filter((e) => e.type !== 'row')
  const labelIdx = pdfLabelColumn(entries, cols)
  const cellText = (values, c) => {
    const v = exportCellValue(values?.[c.key], c)
    return c?.numeric ? fmtNumber(v, c) : pdfText(v, c)
  }
  const footEntries = (footerRows || []).map((r) => ({ type: 'foot', row: r }))
  const rows = [...entries, ...footEntries].map((e) => {
    if (e.type === 'foot') {
      return e.row.map((v, i) => ({
        content: cols[i]?.numeric ? fmtNumber(v, cols[i]) : pdfText(v, cols[i]),
        styles: { fontStyle: 'bold' },
      }))
    }
    if (e.type === 'heading') {
      return [{
        content: winAnsiSafe(e.text),
        colSpan: n,
        styles: { fontStyle: 'bold', fontSize: e.level === 0 ? 9 : 8, cellPadding: indentPadding(e.level) },
      }]
    }
    const bold = e.type === 'subtotal' || e.kind === 'total'
    const steps = e.level + (e.type === 'row' ? (Number(e.depth) || 0) : 0)
    return cols.map((c, i) => {
      const styles = {}
      if (i === labelIdx) styles.cellPadding = indentPadding(steps)
      if (bold) styles.fontStyle = 'bold'
      if (e.type === 'subtotal' && e.line === 0 && c?.numeric) {
        styles.lineWidth = { top: 0.5 }
        styles.lineColor = [156, 163, 175]
      }
      return { content: cellText(e.values, c), styles }
    })
  })
  const spans = keepTogetherBlocks([...entries, ...footEntries])
  const body = []
  let covered = 0
  rows.forEach((r, i) => {
    if (covered > 0) {
      covered -= 1
      body.push(r) // this row's gutter cell is the rowSpan cell above it
      return
    }
    const span = spans.get(i)
    body.push([...r, span ? { content: '', rowSpan: span } : { content: '' }])
    if (span) covered = span - 1
  })
  return { body, entries, labelIdx, spans }
}

/**
 * Build (but don't download) the jsPDF doc — exported so tests can inspect
 * REAL output (page count, back-filled page totals) instead of trusting the
 * wiring; same pattern as buildExcelWorkbook. Async: lazy imports.
 */
export const buildPdf = async (view, scope, meta = {}) => {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ])
  const { headers, matrix, cols } = buildExportMatrix(view, scope)
  // CC-49457 guardrail: SUM-only totals. CC-49860: the whole footer block — a
  // report closing with "Total / PTD" AND "Total / YTD" must print both.
  //
  // NOTE the row basis: totals are derived from `matrix` — the FULL row set for
  // the chosen scope, deliberately NOT the collapse-reduced body below. A
  // collapsed section is a presentation choice, not a change to the dataset, so
  // the grand total must keep describing every row. Deriving it from the printed
  // rows instead would silently understate the total on any collapsed statement,
  // which in a financial report is the worst possible failure mode.
  const footerRows = buildFooterRows(cols, matrix, view)

  // Spec 127 §4.6: a grouped view prints its outline — headings, rows, subtotal
  // lines — with collapse and roll-up already applied by the grid. The foot above
  // is still the full-scope block, never the collapse- or roll-up-reduced body.
  const outline = pdfOutline(view, scope)
  const grouped = outline ? groupedPdfBody(outline, cols, { summaryOnly: !!scope?.summaryOnly, footerRows }) : null

  // ---- The FLAT body (no outline) — unchanged ----------------------------------
  // CC-49843: the printed BODY respects collapsed groups ("what you see is what
  // you print" — the screen and window.print() already do, via the renderer).
  // Callers may opt out with respectCollapse: false.
  const { matrix: collapseAwareMatrix } = buildExportMatrix(view, {
    ...scope,
    respectCollapse: scope?.respectCollapse !== false,
  })

  // CC-49843: "summary only" — grouped reports render ONE row per group
  // (group name + the SERVER subtotal values, verbatim), no leaf rows. The
  // grand-totals foot keeps describing the full dataset via the SUM-only
  // guardrail above. No groups in the view → falls back to the normal body.
  // (A grouped grid's view prints its outline instead — above — whose summary
  // is every heading and every subtotal line at every level.)
  const summaryOnly = !!scope?.summaryOnly && Array.isArray(view?.groups) && view.groups.length > 0
  let body = collapseAwareMatrix
  if (summaryOnly) {
    const firstText = cols.findIndex((c) => !c?.numeric)
    const labelIdx = firstText === -1 ? 0 : firstText
    body = view.groups.map((g) => cols.map((c, i) => {
      if (i === labelIdx) return g.header
      const v = g.footer?.[c.key]
      return v == null ? '' : v
    }))
  }

  // Auto-landscape for wide tables: portrait letter can't fit 8+ columns
  // without breaking header words mid-word.
  const orientation = meta.orientation || (cols.length > 7 ? 'landscape' : 'portrait')
  const doc = new jsPDF({
    orientation,
    unit: 'pt',
    format: 'letter',
  })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const totalPagesExp = '{total_pages_count_string}' // back-filled by putTotalPages

  // ---- Centered masthead (first page only) --------------------------------
  let y = PAGE.marginTop
  // Wrap long lines inside the printable width instead of clipping.
  const centerWrapped = (text, size, opts = {}) => {
    if (!text) return
    doc.setFont('helvetica', opts.style || 'normal')
    doc.setFontSize(size)
    doc.setTextColor(...(opts.color || [17, 24, 39]))
    const lines = doc.splitTextToSize(winAnsiSafe(text), pageW - PAGE.marginX * 2)
    for (const line of lines) {
      doc.text(line, pageW / 2, y, { align: 'center' })
      y += size + 2
    }
    y += opts.gap ?? 2
  }
  // H1 firm name (largest), H2 report name — per Jonathan's 7/8 feedback.
  centerWrapped(meta.firmLine && String(meta.firmLine).toUpperCase(), 18, { style: 'bold', gap: 4 })
  centerWrapped(meta.title, 13, { style: 'bold', color: [55, 65, 81], gap: 6 })
  // Filters-applied block (As-of folds in here; callers compose the line).
  centerWrapped(meta.filtersLine, 9, { color: [107, 114, 128] })
  // Single info row: run timestamp + printed-by, one line.
  centerWrapped(meta.generatedLine, 9, { color: [107, 114, 128] })
  y += 8

  // ---- Table: repeating headers, right-aligned numerics, totals foot ------
  const columnStyles = {}
  cols.forEach((c, i) => {
    if (c?.numeric) columnStyles[i] = { halign: 'right' }
  })
  const fmtRow = (row) => row.map((v, i) => (cols[i]?.numeric ? fmtNumber(v, cols[i]) : pdfText(v, cols[i])))

  // Shared by both bodies: placement, repeating head, last-page foot, fonts, and
  // the "Page X of Y" running foot (the reason browser print CSS is insufficient).
  const shared = {
    startY: y,
    margin: { left: PAGE.marginX, right: PAGE.marginX, top: PAGE.marginTop, bottom: PAGE.marginBottom },
    showHead: 'everyPage', // column headers repeat on every page
    showFoot: 'lastPage', // totals row (CC-49457) only under the last data row
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 4, textColor: [17, 24, 39], overflow: 'linebreak' },
    headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39], fontStyle: 'bold', fontSize: 7, minCellWidth: 36 },
    footStyles: { fillColor: false, textColor: [17, 24, 39], fontStyle: 'bold' },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(107, 114, 128)
      const footY = pageH - PAGE.marginBottom / 2
      doc.text(`Page ${data.pageNumber} of ${totalPagesExp}`, pageW / 2, footY, { align: 'center' })
      if (meta.title) doc.text(winAnsiSafe(meta.title), PAGE.marginX, footY) // running foot, left
      if (meta.generatedLine) doc.text(winAnsiSafe(meta.generatedLine), pageW - PAGE.marginX, footY, { align: 'right' })
    },
  }

  if (grouped) {
    // ---- The GROUPED body (spec 127 §4.6) --------------------------------------
    // One extra, never-drawn "gutter" column carries the keep-together rowSpans;
    // it has no header text. No zebra fill: the headings and subtotal lines are
    // the structure. Numeric HEADERS right-align over their figures; rows never
    // split across pages. The report foot is the body's closing lines (see
    // groupedPdfBody), right-aligned by columnStyles like every body figure.
    const gutter = cols.length
    const gutterStyles = { cellWidth: 1, minCellWidth: 0, cellPadding: 0 }
    autoTable(doc, {
      ...shared,
      head: [[...headers.map(winAnsiSafe), '']],
      body: grouped.body,
      rowPageBreak: 'avoid',
      columnStyles: { ...columnStyles, [gutter]: gutterStyles },
      didParseCell: (data) => {
        if (data.column?.index === gutter) {
          Object.assign(data.cell.styles, gutterStyles)
          return
        }
        if (data.section === 'head' && cols[data.column?.index]?.numeric) data.cell.styles.halign = 'right'
      },
      // The gutter exists only to carry the keep-together spans: never draw it.
      willDrawCell: (data) => (data.column?.index === gutter ? false : undefined),
    })
  } else {
    autoTable(doc, {
      ...shared,
      head: [headers.map(winAnsiSafe)],
      body: body.map(fmtRow),
      foot: footerRows.length > 0 ? footerRows.map(fmtRow) : undefined,
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles,
      // CC-50047: right-align numeric cells in the FOOT as well as the body.
      // `columnStyles` above does not reach the foot — autotable's section styles
      // (footStyles) win there — so every total landed LEFT-aligned while the data
      // rows above it were right-aligned. Confirmed on page 7 of QA's Timekeeper
      // Analysis PDF: 143.59 / 51.90 / 387.86 / 138,735.46 all hug the left edge of
      // their column while the 0.00s above them hug the right. The cell INDEXES
      // were correct all along — each value sits under its proper header — so this
      // is a pure alignment defect, NOT the footer-key mismatch it was first
      // suspected to be, and NOT CC-50024's mixed-unit column.
      didParseCell: footAlignCell(cols),
    })
  }

  if (typeof doc.putTotalPages === 'function') doc.putTotalPages(totalPagesExp)
  return doc
}

// Same object-URL download dance as exportClientView's (unexported) helper —
// a local copy so this module stays independent of the Excel/CSV file's
// internals.
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Browser entry point: build the doc + trigger the download. */
export const exportClientPdf = async (view, scope, filename = 'report-view.pdf', meta = {}) => {
  const doc = await buildPdf(view, scope, meta)
  triggerDownload(doc.output('blob'), filename)
}
